import { useAnimationContext } from "client/packages/animations/AnimationContext"
import { OverlaidAnimationStyle } from "client/packages/animations/types"
import { FC, ReactNode, useState } from "react"
import { ButtonProps } from "shared/components/buttons/types"
import { mediaQuery } from "shared/styles/mixins"
import styled from "styled-components"
import { v4 as uuidv4 } from "uuid"

import { MultiVoteHeart } from "../../animations/animationElements/VotingHeart"

const VotingButton = styled.button<{ $isSelected: boolean }>`
  appearance: none;
  border: none;
  background-color: ${(props) =>
    props.$isSelected
      ? props.theme.voting.buttonSelected
      : props.theme.colors.white};
  color: ${(props) =>
    props.$isSelected ? props.theme.colors.white : props.theme.text.dark};
  font-size: ${(props) => props.theme.text.para};
  margin: 10px;
  border-radius: ${(props) => props.theme.button.borderRadius};
  height: ${(props) => props.theme.input.height};
  display: flex;
  justify-content: center;
  align-items: center;
  word-break: break-word;
  position: relative;
  transition: all 75ms ease-in;
  font-family: ${(props) => props.theme.voting.font};
  font-weight: ${(props) => props.theme.voting.fontWeight};
  font-style: ${(props) => props.theme.voting.fontStyle};
  -webkit-tap-highlight-color: transparent; // to remove the blue 'highlight' on android

  p {
    // specifies max number of lines, to truncate with ellipsis
    -webkit-line-clamp: 2;
    overflow: hidden;
    word-break: break-word;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    margin: 0;
    user-select: none;
  }

  // THIS WIDTH ACCOMMODATES 45-CHARACTER ANSWERS.
  width: 300px;

  &:hover {
    background-color: ${(props) => props.theme.colors.neutral};
  }

  &:active {
    background-color: ${(props) => props.theme.voting.buttonSelected};
  }

  &:focus-within {
    outline-style: none !important;
    outline-color: transparent !important;
  }

  ${mediaQuery.mobileDevice} {
    height: ${(props) => props.theme.mobile.input.height};
    // THIS WIDTH ACCOMMODATES 45-CHARACTER ANSWERS AT MOBILE FONT SIZE
    width: 331px;

    // But to fit on the screen in these situations, we need to get TALL not WIDE
    // Mobile portrait (when <= 340px wide), and mobile landscape (when <= 705px wide)
    @media screen and (max-width: 340px),
      (max-height: 450px) and (max-width: 705px) {
      width: 260px;
      height: 58px;
      padding: 1px 12px;
    }
  }
`

export interface VotingButtonProps extends ButtonProps {
  isSelected?: boolean
  showAnimation?: boolean
  children?: ReactNode
}

const VotingAnimationButton: FC<VotingButtonProps> = ({
  onClick = () => {},
  disabled = false,
  isSelected = false,
  showAnimation = false,
  children,
}) => {
  const [visibleVotes, updateVisibleVotes] = useState<string[]>([])
  const { setOverlaidAnimation } = useAnimationContext()

  const addVoteIcon = () => {
    const newState = [...visibleVotes, uuidv4()]
    updateVisibleVotes(newState)
  }

  const handleClick = () => {
    if (showAnimation) {
      addVoteIcon()
    } else {
      setOverlaidAnimation(OverlaidAnimationStyle.HEART)
    }

    onClick()
  }

  const removeVoteIcon = (id: string) => {
    const prevState = [...visibleVotes]
    const newState = prevState.filter((iconId) => iconId !== id)
    updateVisibleVotes(newState)
  }

  return (
    <VotingButton
      type="button"
      data-robot="voting-option"
      onClick={handleClick}
      $isSelected={isSelected}
      disabled={disabled}
    >
      <p>{children}</p>
      {visibleVotes.map((voteId) => (
        <MultiVoteHeart
          key={voteId}
          onComplete={() => removeVoteIcon(voteId)}
        />
      ))}
    </VotingButton>
  )
}

export default VotingAnimationButton
